import type { ReactNode } from 'react';

/**
 * **Le bloc d'une page qui PRÉSENTE — et il porte la densité AÉRÉE du site vitrine.**
 *
 * `DESIGN.md` donne trois densités, une par surface, et celle-ci est la plus large :
 * « **site vitrine — aérée** — atteint **une fois**, par des gens qui **ne connaissent
 * pas le produit** ». C'est la seule règle de charte que ce composant porte, et c'est
 * pour cela qu'il existe : sept pages la répètent, et **une densité recopiée sept fois
 * diverge à la première page écrite un jour de fatigue**.
 *
 * **AUCUNE VALEUR EN DUR, ET LES DEUX PLAFONDS VIENNENT DE `tokens.css`.** `--largeur-
 * espace` pour un bloc large, `--largeur-etroite` pour du texte suivi — « la charte fixait
 * un **plancher** de largeur, jamais un plafond, et un texte qui court sur toute la largeur
 * d'un écran de bureau ne se lit pas ».
 *
 * **CE COMPOSANT NE REFUSE AUCUN APPAREIL, ET C'EST LA BORNE EXACTE DE F-73** : « les sept
 * pages qui présentent **ne refusent rien et s'adaptent à tout** ; la page de connexion
 * refuse le téléphone. Le plancher de 1024 points **ne lie pas le site qui présente**, il
 * lie l'application et la porte qui y mène. » On ne trouvera donc **aucune variante
 * `plancher:`** ici — elle vit sur la seule page qui refuse.
 */
type Props = {
  /** Le titre du bloc. Absent sur un bloc d'ouverture, qui porte celui de la page. */
  readonly titre?: string;
  /** `etroite` pour du texte suivi, `large` pour une grille ou un média. */
  readonly largeur?: 'etroite' | 'large';
  readonly children: ReactNode;
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **TOUTES LES SECTIONS PARTAGENT LE MÊME BORD GAUCHE, ET `largeur` NE CHANGE QUE LA
 * LONGUEUR DE LIGNE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **C'est un défaut constaté à l'écran, et corrigé pour cela.** La première forme de ce
 * composant centrait **la section entière** dans `max-w-etroite` ou `max-w-espace` selon la
 * propriété : deux sections voisines n'avaient alors **pas le même bord gauche**, et le texte
 * sautait de cent vingt points d'un bloc au suivant en descendant la page. Rien ne le
 * signalait — les deux largeurs viennent de `tokens.css`, le lint et le typage passaient, et
 * le défaut ne se voit **que sur une page assemblée**.
 *
 * La forme retenue centre **toujours** le conteneur à `--largeur-espace`, et `etroite` borne
 * la longueur de ligne **à l'intérieur**, alignée à gauche. La longueur de ligne reste réglée
 * — « un texte qui court sur toute la largeur d'un écran de bureau ne se lit pas » — et la
 * colonne de la page ne bouge plus.
 */
export function Section({ titre, largeur = 'large', children }: Props) {
  return (
    <section className="px-6 py-12">
      <div className="mx-auto flex max-w-espace flex-col gap-6">
        {titre !== undefined && (
          <h2 className="text-24 font-weight-800 leading-serre tracking-titre text-texte-fort">
            {titre}
          </h2>
        )}
        <div
          className={[
            'flex flex-col gap-6',
            largeur === 'etroite' ? 'max-w-etroite' : '',
          ].join(' ')}
        >
          {children}
        </div>
      </div>
    </section>
  );
}

/**
 * **Un paragraphe de la surface publique.** Il existe pour la même raison que `Section` :
 * la taille, l'interligne et la couleur du corps de texte sont trois valeurs de charte, et
 * les recopier à chaque paragraphe de sept pages est le chemin le plus court vers une page
 * qui n'a pas la même que les autres.
 */
export function Paragraphe({ children }: { readonly children: ReactNode }) {
  return (
    <p className="text-16 leading-large text-texte-faible">{children}</p>
  );
}
